import { ToastAlertTypes } from '@genial/components';
import { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  ScrollView,
  NativeSyntheticEvent,
  TextInputChangeEventData,
} from 'react-native';
import { useRecoilValue } from 'recoil';

import { useToastCreator } from '../../../hooks/useToastCreator';
import { sessionState } from '../../../store/session/atom';
import { createMessagePayloadStepFunctions } from '../utils/payload';

export type SenderType = 'User' | 'Bot';

type MessagesProps = {
  text: string;
  sender: SenderType;
};

const URL_FETCH_RESPONSE =
  'https://u3xoms55diflclirq4pktlxeia0rzisj.lambda-url.us-east-1.on.aws/bedrock_claude_messages_api/'; /*"http://localhost:8000/send-message";*/

export const errorSendMessage: [ToastAlertTypes.Type, string, string] = [
  'error',
  'Opa! Algo parece errado.',
  'Ocorreu um erro ao enviar sua mensagem,\nverifique sua conexão com a internet e\ntente novamente em instantes.',
];

const INITIAL_MESSAGE: MessagesProps[] = [
  {
    text: 'Olá! Bem-vindo ao suporte da Genial Investimentos. Como posso ajudar você hoje?',
    sender: 'Bot',
  },
];

export const useChat = () => {
  const { userProfile } = useRecoilValue(sessionState);
  const { showToast } = useToastCreator('toast-error-send-message');

  const [isHovered, setIsHovered] = useState(false);
  const [chatIsOpen, setChatIsOpen] = useState(false);
  const [messages, setMessages] = useState<MessagesProps[]>(INITIAL_MESSAGE);
  const [messageIsSending, setMessageIsSending] = useState(false);
  const [input, setInput] = useState('');

  const completeTextRef = useRef('');
  const chatContainerRef = useRef<ScrollView>(null);
  const animatedWidth = useRef(new Animated.Value(40)).current;

  const sendButtonIsDisabled = messageIsSending || input.length === 0;

  const sendMessageService = async (userMessage: string) => {
    completeTextRef.current = '';

    const payload = createMessagePayloadStepFunctions({
      userMessage,
      userId: userProfile?.idUsuarioLogado,
    });

    const response = await fetch(URL_FETCH_RESPONSE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error('Desculpe, não foi possível processar sua solicitação. Tente novamente.');
    }

    if (!response.body) {
      throw new Error(
        'Desculpe, não foi possível processar a resposta do servidor. Tente novamente.'
      );
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let done = false;
    let text = '';

    while (!done) {
      const { value, done: readerDone } = await reader.read();
      done = readerDone;
      text = decoder.decode(value, { stream: true });
      completeTextRef.current += text;
      setMessages((prevMessages) => {
        const lastMessage = prevMessages[prevMessages.length - 1];
        if (lastMessage && lastMessage.sender === 'Bot') {
          return [
            ...prevMessages.slice(0, prevMessages.length - 1),
            { text: completeTextRef.current, sender: 'Bot' },
          ];
        }
        return [...prevMessages, { text: completeTextRef.current, sender: 'Bot' }];
      });
    }
  };

  const handleSendMessage = async () => {
    try {
      if (input.trim()) {
        setMessageIsSending(true);
        const userMessage = input;

        setMessages([...messages, { text: userMessage, sender: 'User' }]);
        setInput('');
        await sendMessageService(userMessage);
      }
    } catch (error) {
      console.error('Error sending message to API:', error);
      showToast(...errorSendMessage);
    } finally {
      setMessageIsSending(false);
    }
  };

  const onChangeSetInput = (e: NativeSyntheticEvent<TextInputChangeEventData>) => {
    setInput(e.nativeEvent.text);
  };

  const onToggleChatState = () => {
    setChatIsOpen((prev) => !prev);
    onHoverOut();
  };

  const onHoverIn = () => {
    setIsHovered(true);
    Animated.timing(animatedWidth, {
      toValue: 170,
      duration: 300,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
  };

  const onHoverOut = () => {
    setIsHovered(false);
    Animated.timing(animatedWidth, {
      toValue: 40,
      duration: 300,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
  };

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollToEnd({ animated: true });
    }
  }, [messages]);

  return {
    handleSendMessage,
    input,
    messages,
    onChangeSetInput,
    chatContainerRef,
    messageIsSending,
    chatIsOpen,
    onToggleChatState,
    sendButtonIsDisabled,
    onHoverIn,
    onHoverOut,
    animatedWidth,
    isHovered,
  };
};
